/**
 * AmazonStoreAdmin — CRUD for the "De compras pel Món" affiliate storefront.
 * Admin pastes any Amazon URL; the backend normalises it with `tag=Tarraco21`.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const empty = () => ({
  category: 'palos',
  subcategory: '',
  title: '',
  description: '',
  image_url: '',
  price: '',
  amazon_url: '',
  visible: true,
  featured: false,
  order: 0,
});

export const AmazonStoreAdmin = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [filter, setFilter] = useState('all');
  const [previewTag, setPreviewTag] = useState('');

  const authHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const load = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/zona-pro/amazon/products`, authHeader()),
        axios.get(`${API_URL}/api/amazon/categories`),
      ]);
      setProducts(prodRes.data?.products || []);
      setCategories(catRes.data?.categories || []);
    } catch {
      toast.error('No s\'han pogut carregar els productes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const previewUrl = async (url) => {
    if (!url) { setPreviewTag(''); return; }
    try {
      const r = await axios.get(
        `${API_URL}/api/zona-pro/amazon/preview-url`,
        { ...authHeader(), params: { url } },
      );
      setPreviewTag(r.data?.tagged || '');
    } catch {
      setPreviewTag('');
    }
  };

  const save = async (p) => {
    if (!p.title?.trim() || !p.image_url?.trim() || !p.amazon_url?.trim()) {
      toast.error('Títol, imatge i URL d\'Amazon són obligatoris');
      return;
    }
    try {
      if (p.id) {
        await axios.put(`${API_URL}/api/zona-pro/amazon/products/${p.id}`, p, authHeader());
      } else {
        await axios.post(`${API_URL}/api/zona-pro/amazon/products`, p, authHeader());
      }
      toast.success('Guardat');
      setDraft(null);
      setPreviewTag('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminar aquest producte?')) return;
    try {
      await axios.delete(`${API_URL}/api/zona-pro/amazon/products/${id}`, authHeader());
      toast.success('Eliminat');
      await load();
    } catch { toast.error('Error'); }
  };

  const toggleVisible = async (p) => {
    try {
      await axios.put(
        `${API_URL}/api/zona-pro/amazon/products/${p.id}`,
        { visible: !p.visible },
        authHeader(),
      );
      await load();
    } catch { toast.error('Error'); }
  };

  const filtered = filter === 'all' ? products : products.filter((p) => p.category === filter);

  return (
    <div className="space-y-4" data-testid="amazon-store-admin">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-slate-600 flex-1">
          Enganxa qualsevol URL d&apos;Amazon i el sistema aplicarà automàticament el teu tag d&apos;afiliat{' '}
          <code className="px-1.5 py-0.5 bg-amber-100 rounded text-amber-800">Tarraco21</code>.
        </p>
        <Button
          onClick={() => { setDraft(empty()); setPreviewTag(''); }}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="amazon-create-btn"
        >
          + Nou producte
        </Button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          data-testid="amazon-filter-all"
        >
          Tots ({products.length})
        </Button>
        {categories.map((c) => (
          <Button
            key={c.slug}
            size="sm"
            variant={filter === c.slug ? 'default' : 'outline'}
            onClick={() => setFilter(c.slug)}
            data-testid={`amazon-filter-${c.slug}`}
          >
            {c.emoji} {c.label_ca} ({products.filter((p) => p.category === c.slug).length})
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Carregant…</div>
      ) : filtered.length === 0 && !draft ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            <p>Encara no hi ha productes en aquesta categoria.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Card key={p.id} data-testid={`amazon-row-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-20 h-20 object-cover bg-slate-100 rounded"
                    onError={(e) => { e.target.style.opacity = 0.3; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {p.category}{p.subcategory ? ` · ${p.subcategory}` : ''}
                    </div>
                    {p.price && <div className="text-sm font-semibold text-emerald-700">{p.price}</div>}
                    <div className="text-[11px] text-slate-400 mt-1">
                      {p.visible ? '👁️ Visible' : '🚫 Amagat'}{p.featured ? ' · ⭐ Destacat' : ''}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => toggleVisible(p)} data-testid={`amazon-toggle-${p.id}`}>
                    {p.visible ? 'Amagar' : 'Mostrar'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setDraft(p); setPreviewTag(p.amazon_url); }} data-testid={`amazon-edit-${p.id}`}>
                    Editar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(p.id)} data-testid={`amazon-del-${p.id}`}>
                    Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {draft && (
        <Card className="border-emerald-300 mt-4" data-testid="amazon-draft-card">
          <CardContent className="p-4 space-y-3">
            <h4 className="font-medium">{draft.id ? 'Editar producte' : 'Nou producte'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Categoria *</Label>
                <select
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  data-testid="amazon-draft-category"
                >
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.emoji} {c.label_ca}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Subcategoria</Label>
                <Input
                  value={draft.subcategory || ''}
                  onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })}
                  placeholder="p.e. driver, putter…"
                  data-testid="amazon-draft-subcategory"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Títol *</Label>
                <Input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Nom del producte"
                  data-testid="amazon-draft-title"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Descripció</Label>
                <Textarea
                  value={draft.description || ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Breu descripció visible als usuaris"
                  rows={2}
                  data-testid="amazon-draft-description"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">URL de la imatge *</Label>
                <Input
                  value={draft.image_url}
                  onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
                  placeholder="https://…"
                  data-testid="amazon-draft-image"
                />
              </div>
              <div>
                <Label className="text-xs">Preu (text lliure)</Label>
                <Input
                  value={draft.price || ''}
                  onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                  placeholder="39,90 €"
                  data-testid="amazon-draft-price"
                />
              </div>
              <div>
                <Label className="text-xs">Ordre</Label>
                <Input
                  type="number"
                  value={draft.order}
                  onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value) || 0 })}
                  data-testid="amazon-draft-order"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">URL d&apos;Amazon * (s&apos;afegirà tag=Tarraco21 automàticament)</Label>
                <Input
                  value={draft.amazon_url}
                  onChange={(e) => {
                    setDraft({ ...draft, amazon_url: e.target.value });
                    previewUrl(e.target.value);
                  }}
                  placeholder="https://www.amazon.es/dp/…"
                  data-testid="amazon-draft-url"
                />
                {previewTag && (
                  <div className="mt-1 text-[11px] text-emerald-700 break-all bg-emerald-50 rounded px-2 py-1">
                    ✓ {previewTag}
                  </div>
                )}
              </div>
              <div className="flex items-end gap-2">
                <input
                  id="amz-visible"
                  type="checkbox"
                  checked={draft.visible}
                  onChange={(e) => setDraft({ ...draft, visible: e.target.checked })}
                  data-testid="amazon-draft-visible"
                />
                <Label htmlFor="amz-visible" className="cursor-pointer">Visible al públic</Label>
              </div>
              <div className="flex items-end gap-2">
                <input
                  id="amz-featured"
                  type="checkbox"
                  checked={draft.featured}
                  onChange={(e) => setDraft({ ...draft, featured: e.target.checked })}
                  data-testid="amazon-draft-featured"
                />
                <Label htmlFor="amz-featured" className="cursor-pointer">Destacat ⭐</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setDraft(null); setPreviewTag(''); }} data-testid="amazon-cancel-btn">
                Cancel·lar
              </Button>
              <Button
                onClick={() => save(draft)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="amazon-save-btn"
              >
                Guardar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AmazonStoreAdmin;
